import {
  Box,
  ButtonGroup,
  Container,
  Drawer,
  DrawerContent,
  DrawerOverlay,
  Flex,
  HStack,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  theme,
  useBreakpointValue,
  useDisclosure,
} from '@chakra-ui/react';
import { Icon } from '@common/components/Icon';
import { Image } from '@common/components/Image';
import { useInboxModel } from '@common/models/InboxModel';
import { useInvestorAccountModel } from '@common/models/InvestorAccountModel';
import support from '@common/support';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { ROUTES } from 'src/apps/invest/config';
import { InvestorCard } from './InvestorCard';
import { NavbarMenuItem } from './NavbarMenuItem';
import { NavbarSubmenu } from './NavbarSubmenu';
import { NavbarSubmenuItem } from './NavbarSubmenuItem';
import { PartnerLogo } from './PartnerLogo';
import { Sidebar } from './Sidebar';
import { ToggleButton } from './ToggleButton';
import { UserProfile } from './UserProfile';

export const Navbar = () => {
  const isDesktop = useBreakpointValue({ base: false, lg: true });
  const { isOpen, onToggle, onClose } = useDisclosure();
  const inboxModel = useInboxModel();
  const { investorAccount } = useInvestorAccountModel();
  const inboxCount =
    inboxModel.alerts.length +
    inboxModel.messages.filter((m) => !m.viewed && !m.dismissed).length +
    inboxModel.newIntercomUnreadMessageCount;
  const inboxCountColor =
    inboxModel.alerts.length > 0 || inboxModel.newIntercomUnreadMessageCount > 0
      ? theme.colors.red[400]
      : theme.colors.blue[400];

  return (
    <Box as="nav" bg="bg-surface" zIndex={100} position="sticky" w="100%" top="0">
      <Container py={{ base: '3', lg: '5' }} maxW={'100%'} w="100%">
        <Flex justify="space-between">
          <HStack spacing="8">
            <Link to={ROUTES.dashboard}>
              <Image image="logo-beta" width={['100px', '166px']} cursor="pointer" />
            </Link>
            {isDesktop && (
              <ButtonGroup variant="ghost" spacing="4">
                <NavbarMenuItem icon={'grid-2-2'} label={'Dashboard'} route={ROUTES.dashboard} />
                <NavbarSubmenu icon={'building'} label={'Invest'}>
                  <NavbarSubmenuItem
                    description="Own single-family rentals with income and appreciation potential."
                    image="sfr-icon"
                    label={'Home Shares'}
                    route={`${ROUTES.properties}`}
                    tag="Equity"
                  />
                  <NavbarSubmenuItem
                    description="Build income with a diversified portfolio of fully managed real estate debt and income equity."
                    image="cash-flow-icon"
                    label={'Cash Flow'}
                    route={`${ROUTES.offeringsCashflow}`}
                    tag="Debt"
                  />
                </NavbarSubmenu>
              </ButtonGroup>
            )}
          </HStack>
          {isDesktop ? (
            <HStack alignItems={'center'} justifyContent={'center'}>
              <NavbarMenuItem icon={'map-location-dot'} iconVariant="solid" label={'Roadmap'} route={ROUTES.roadmap} />

              {inboxCount > 0 && (
                <Menu>
                  <MenuButton aria-label="Options" paddingRight={'10px'}>
                    <HStack py="5px" gap="5px" pr="10px" justifyContent={'space-between'} position="relative">
                      <Box
                        display={'flex'}
                        alignItems={'center'}
                        justifyContent={'center'}
                        bgColor={inboxCountColor}
                        fontSize="11px"
                        color="white"
                        position="absolute"
                        top="-5px"
                        right="0px"
                        minWidth="18px"
                        height="18px"
                        px="5px"
                        textOverflow={'ellipsis'}
                        overflow={'hidden'}
                        borderRadius={'20px'}>
                        {inboxCount > 99 ? '99+' : inboxCount}
                      </Box>
                      <Icon icon="inbox" variant="regular" boxSize={5} />
                    </HStack>
                  </MenuButton>
                  <MenuList>
                    {inboxModel.alerts.slice(0, 10).map((alert) => (
                      <MenuItem
                        as={Link}
                        to={ROUTES[alert.navigateTo] as string}
                        key={alert.title}
                        icon={<Icon color={'gray.600'} fontSize={'15px'} icon={alert.alertIcon} />}>
                        {alert.title}
                      </MenuItem>
                    ))}
                    {inboxModel.messages
                      .slice(0, 10)
                      .filter((m) => !m.viewed && !m.dismissed)
                      .map((message) => (
                        <MenuItem
                          as={Link}
                          to={message?.id ? (ROUTES.inboxMessage(message.id) as string) : ROUTES.inbox}
                          key={message.title}
                          icon={<Icon color={'gray.600'} fontSize={'15px'} icon={'inbox'} />}>
                          {message.title}
                        </MenuItem>
                      ))}
                    <MenuDivider />
                    <MenuItem
                      as={Link}
                      to={ROUTES.inbox}
                      justifyContent={'center'}
                      _hover={{
                        backgroundColor: 'transparent',
                      }}>
                      {inboxCount > 10 ? '...View All' : 'Go to Inbox'}
                    </MenuItem>
                  </MenuList>
                </Menu>
              )}

              <Menu>
                <MenuButton aria-label="Options">
                  <HStack
                    borderColor={'gray.100'}
                    borderRadius={'100'}
                    borderWidth={'1px'}
                    py="5px"
                    gap="5px"
                    pr="10px"
                    justifyContent={'space-between'}>
                    <UserProfile />
                    <Icon icon="caret-down" boxSize={5} />
                  </HStack>
                </MenuButton>
                <MenuList pt="0px" w="248px">
                  {investorAccount ? (
                    <Box pb="7px">
                      <InvestorCard investorAccount={investorAccount} />
                    </Box>
                  ) : null}
                  <MenuItem
                    pl="24px"
                    as={Link}
                    to={ROUTES.activity}
                    icon={<Icon icon={'list-timeline'} boxSize={'15px'} color="gray.300" variant="solid" />}>
                    Activity
                  </MenuItem>
                  <MenuItem
                    as={Link}
                    to={ROUTES.accountInformation}
                    icon={<Icon icon={'id-card'} boxSize={'15px'} color="gray.300" variant="regular" />}
                    pl="24px">
                    Personal Information
                  </MenuItem>
                  <MenuItem
                    as={Link}
                    to={ROUTES.profile}
                    icon={<Icon icon={'scale-unbalanced'} boxSize={'15px'} color="gray.300" variant="regular" />}
                    pl="24px">
                    Investor Profile
                  </MenuItem>
                  <MenuItem
                    as={Link}
                    to={ROUTES.inbox}
                    flexDirection={'row'}
                    justifyContent={'space-between'}
                    icon={<Icon icon={'inbox'} boxSize={'15px'} color="gray.300" variant="regular" />}
                    pl="24px">
                    Inbox
                    {inboxCount > 0 && (
                      <Box
                        display={'inline-flex'}
                        alignItems={'center'}
                        justifyContent={'center'}
                        bgColor={inboxCountColor}
                        fontSize="11px"
                        color="white"
                        marginLeft="8px"
                        minWidth="15px"
                        height="15px"
                        px="5px"
                        textOverflow={'ellipsis'}
                        overflow={'hidden'}
                        borderRadius={'20px'}>
                        {inboxCount > 99 ? '99+' : inboxCount}
                      </Box>
                    )}
                  </MenuItem>
                  <MenuItem
                    as={Link}
                    to={ROUTES.autoInvestSchedule}
                    icon={<Icon icon={'calendar'} boxSize={'16px'} color="gray.300" variant="regular" />}
                    pl="24px">
                    Auto Invest Schedule
                  </MenuItem>
                  <MenuItem
                    as={Link}
                    to={ROUTES.bankAccounts}
                    icon={<Icon icon={'building'} boxSize={'16px'} color="gray.300" variant="regular" />}
                    pl="24px">
                    Bank Accounts
                  </MenuItem>
                  <MenuItem
                    as={Link}
                    to={ROUTES.documents}
                    icon={<Icon icon={'file-invoice'} boxSize={'16px'} color="gray.300" variant="regular" />}
                    pl="24px">
                    Documents
                  </MenuItem>
                  <MenuItem
                    icon={<Icon icon={'comment'} boxSize={'16px'} color="gray.300" variant="regular" />}
                    onClick={support.show}
                    pl="24px">
                    Help & Support
                  </MenuItem>
                  {investorAccount?.partner && (
                    <>
                      <MenuDivider />
                      <PartnerLogo />
                    </>
                  )}
                  <MenuDivider />
                  <MenuItem
                    as={Link}
                    to={ROUTES.signout}
                    icon={<Icon icon="right-from-bracket" boxSize={'16px'} color="gray.300" variant="regular" />}
                    pl="24px">
                    Sign Out
                  </MenuItem>
                </MenuList>
              </Menu>
            </HStack>
          ) : (
            <>
              <ToggleButton isOpen={isOpen} aria-label="Open Menu" onClick={onToggle} />
              <Drawer
                isOpen={isOpen}
                placement="left"
                onClose={onClose}
                isFullHeight={true}
                preserveScrollBarGap={true}>
                <DrawerOverlay />
                <DrawerContent>
                  <Sidebar />
                </DrawerContent>
              </Drawer>
            </>
          )}
        </Flex>
      </Container>
    </Box>
  );
};
